import NextApp, {AppContext, AppProps, AppInitialProps} from 'next/app';
import {DataProvider, DataConfig} from '@sail/data';
import {
  IntlConfig,
  IntlProvider,
  Locale,
  LocaleData,
  browserTimeZone,
} from '@sail/intl';
import {
  ObservabilityConfig,
  ObservabilityProvider,
  ErrorBoundary,
} from '@sail/observability';
import {NavigationConfig, NavigationProvider} from '@sail/navigation';
import {useMemo, useState} from 'react';
import Head from 'next/head';
import {
  DEFAULT_PREFIX,
  GeoIPCountryProvider,
  GlobalizationContextProvider,
  getPrefixData,
} from 'mkt-ssr/lib/globalization';
import {STRIPE_ENV, getStripeEnv, isServer} from 'mkt-ssr/lib/env';
import {routes} from 'mkt-ssr/lib/routing/AllRoutes.routes';
import {LinkResolverProvider} from 'mkt-ssr/lib/routing/LinkResolver';
import {
  LocaleDataWithoutTimezones,
  getDataForLocale,
} from 'mkt-ssr/lib/globalization/localeDataLoaders';
import {HostData, getHostData} from 'mkt-ssr/lib/routing/getHostData';
import {
  ContentfulConfig,
  ContentfulContextProvider,
  getContentfulConfig,
} from 'mkt-ssr/lib/contentful/config';
import {
  ExperimentData,
  ExperimentsProvider,
  getExperimentData,
} from 'mkt-ssr/lib/experiments';
import {AppErrorView} from 'mkt-ssr/components/AppErrorView';
import {AnalyticsClientProvider} from 'mkt-ssr/lib/analytics';
import {FormAugmentationProvider} from 'mkt-ssr/lib/forms';
import {FontProvider} from 'mkt-ssr/lib/assets/fonts';
import {HostDataProvider} from 'mkt-ssr/lib/assets/HostDataProvider';

// NB: Order is very important here. The side-effect of
// setting the nonce *must* be imported before any CSS files
// or components containing CSS modules.
import 'mkt-ssr/lib/setWebackNonceInDev';
import 'mkt-ssr/app.css';
import {Notifications} from 'mkt-ssr/lib/notifications/Notifications/Notifications';
import {UiEngineRootProvider} from 'mkt-ssr/components/UiEngineProvider';

const OBSERVABILITY_CONFIG: ObservabilityConfig = {
  project: 'mkt',
  service: 'mkt-srv',
  errors: {
    projects: {
      mkt:
        getStripeEnv() === STRIPE_ENV.PROD
          ? 'https://7cd38b0eb2b348b39a6002cc768f91c7@errors.stripe.com/376'
          : 'https://a48b5edd654241538771b88e0361296e@qa-errors.stripe.com/298',
    },
    disabled:
      isServer() ||
      (window.location.search.includes('__auth') &&
        window.location.search.includes('__disableErrorCapture')),
    captureUnhandledErrors:
      !isServer() &&
      !(
        window.location.search.includes('__auth') &&
        window.location.search.includes('__disableErrorCapture')
      ),
    expectedJsErrors: [
      // Common errors from mkt-dev-tools
      'Uncaught (in promise) Error: Could not establish connection. Receiving end does not exist.',
      'Unchecked runtime.lastError: Could not establish connection. Receiving end does not exist.',
    ],
  },
  analytics: {
    disabled:
      isServer() ||
      (window.location.search.includes('__auth') &&
        window.location.search.includes('__disableAnalytics')),
  },
  metrics: {
    disabled: true,
  },
};

interface CustomAppProps {
  localeData: LocaleDataWithoutTimezones;
  hostData: HostData;
  devOnlyNonce: string | undefined;
  contentfulConfig: ContentfulConfig;
  devDebugMode: boolean;
  devToolsExperimentMetadata: string | undefined;
  experimentData: ExperimentData;
}

export default function App({
  Component,
  pageProps,
  router: {locale, defaultLocale, asPath},
  hostData,
  localeData,
  devOnlyNonce,
  contentfulConfig,
  devDebugMode,
  devToolsExperimentMetadata,
  experimentData,
}: AppProps & CustomAppProps) {
  // For reasons that are unclear to me NextJS doesn't
  // provide a way to get just the resolved path
  // without the replacements inline and without the query parameters.
  // As such we need to extract that data ourselves.
  const resolvedPath = useMemo(
    () => new URL(asPath, `https://${hostData.resolved}`).pathname,
    [hostData, asPath],
  );
  const prefixData = useMemo(() => getPrefixData(locale || ''), [locale]);

  const dataConfig: DataConfig = useMemo(
    () => ({
      observability: {expectedApiErrors: [], mutedApiErrors: []},
    }),
    [],
  );

  const intlConfig = useMemo<IntlConfig>(
    () => ({
      locale: prefixData.locale.bcp47,
      timeZone: browserTimeZone,
      localeData,
    }),
    [localeData, prefixData.locale.bcp47],
  );
  const [href, setHref] = useState(
    locale === defaultLocale ? resolvedPath : `/${locale + resolvedPath}`,
  );
  const config: NavigationConfig = {href, setHref, routes};

  // NB: Right now this is a rollup up
  // of *almost* all of our "global" providers.
  // This behavior will very likley eventually
  // be moved to the HOC returned by `createPage`
  // which will allow for behavior such as overriding
  // configuration for behaviors such as observability,
  // etc. on a page-by-page basis without requring
  // making assumptions about "page component shape" here.
  return (
    <ObservabilityProvider {...OBSERVABILITY_CONFIG}>
      <DataProvider config={dataConfig}>
        <NavigationProvider config={config}>
          <IntlProvider config={intlConfig}>
            <GlobalizationContextProvider value={prefixData}>
              <GeoIPCountryProvider>
                <ContentfulContextProvider value={contentfulConfig}>
                  <HostDataProvider value={hostData}>
                    <LinkResolverProvider
                      actualHost={hostData.requested}
                      resolvedHost={hostData.resolved}
                    >
                      <AnalyticsClientProvider>
                        <ExperimentsProvider experimentData={experimentData}>
                          <FontProvider>
                            <FormAugmentationProvider>
                              <UiEngineRootProvider>
                                <Head>
                                  {devOnlyNonce ? (
                                    <meta
                                      name="x-stripe-dev-nonce"
                                      content={devOnlyNonce}
                                    />
                                  ) : null}

                                  {/* To make mkt-dev-tools chrome extension work with experiment data */}
                                  {/* ======================================== */}
                                  {devDebugMode ? (
                                    <script
                                      type="application/json"
                                      id="experiments-metadata"
                                    >
                                      {devToolsExperimentMetadata}
                                    </script>
                                  ) : null}
                                  {/* ======================================== */}
                                </Head>
                                <ErrorBoundary
                                  uuid="3Y954S00"
                                  errorView={AppErrorView}
                                >
                                  <Component {...pageProps} />
                                </ErrorBoundary>
                                <ErrorBoundary
                                  uuid="3Y954S01"
                                  errorView={() => null}
                                >
                                  <Notifications />
                                </ErrorBoundary>
                              </UiEngineRootProvider>
                            </FormAugmentationProvider>
                          </FontProvider>
                        </ExperimentsProvider>
                      </AnalyticsClientProvider>
                    </LinkResolverProvider>
                  </HostDataProvider>
                </ContentfulContextProvider>
              </GeoIPCountryProvider>
            </GlobalizationContextProvider>
          </IntlProvider>
        </NavigationProvider>
      </DataProvider>
    </ObservabilityProvider>
  );
}

App.getInitialProps = async (
  context: AppContext,
): Promise<AppInitialProps & CustomAppProps> => {
  const ctx = await NextApp.getInitialProps(context);
  const prefixData = getPrefixData(
    context.router.locale ||
      context.router.defaultLocale ||
      DEFAULT_PREFIX.prefix,
  );
  const locale: Locale = prefixData.locale.bcp47;
  const {locale: fullLocaleData} = await getDataForLocale(locale);

  // This code should only run on the server, so we import here to avoid pulling server-side node
  // modules into the client bundle (next.config.js is set to ignore this import in the client JS bundle)
  let pageMessages: Record<string, string> = {};
  if (isServer()) {
    const {getPageMessagesForRoute, getLocaleBundlePath} = await import(
      '@stripe-internal/nautilus-ssr-server'
    );
    const localeBundlePath = getLocaleBundlePath('intl/bundles', locale);
    pageMessages = getPageMessagesForRoute(context, localeBundlePath);
  }

  const trimmedLocaleData: LocaleData = {
    ...fullLocaleData,
    messages: pageMessages,
  } as LocaleData;
  return {
    ...ctx,
    hostData: getHostData(context.ctx.req),
    localeData: trimmedLocaleData,
    contentfulConfig: getContentfulConfig(context.ctx.req?.headers),
    devDebugMode:
      context.ctx.req?.headers['x-dev-debug-mode']?.toString() === 'true',
    devOnlyNonce: context.ctx.req?.headers['x-nonce']?.toString(),
    devToolsExperimentMetadata:
      context.ctx.req?.headers['x-dev-tools-experiment-metadata']?.toString(),
    experimentData: getExperimentData(context.ctx.req?.headers),
  };
};
